import { Chart, ChartEvent, Plugin } from 'chart.js';
import { DerichebourgColorEnum } from '@shared/enums/color-derichebourg-enum';
import { findMatchingXLabels, getLabelLines, getXCategoryScale, ResolvedXLabelLayout, resolveXLabelLayout } from '../chart-utils/x-label-geometry';

/**
 * Public options consumed from `chart.options.plugins.xLabelHover`.
 */
type XLabelHoverOptions = {
    enabled?: boolean;
    onLabelHover?: (index: number, label: string | string[]) => void;
    onLabelLeave?: () => void;
};

/**
 * Internal state used by the plugin between events and draw calls.
 */
type HoverStateChart = Chart<'bar', number[], string> & {
    _xLabelHoverIndex?: number | null;
    _xLabelHoverLabel?: string | string[] | null;
    _xLabelHoverPoint?: { x: number; y: number } | null;
};

type HoverPluginOptionsHost = {
    plugins?: {
        xLabelHover?: XLabelHoverOptions;
    };
};

/**
 * Hover plugin is globally registered but explicitly enabled per chart.
 */
function isPluginEnabled(chart: Chart<'bar', number[], string>): boolean {
    const opts = chart.options as HoverPluginOptionsHost;
    return opts.plugins?.xLabelHover?.enabled === true;
}

function hexToRgba(hex: string, alpha: number): string {
    const normalized = hex.replace('#', '');
    const red = parseInt(normalized.slice(0, 2), 16);
    const green = parseInt(normalized.slice(2, 4), 16);
    const blue = parseInt(normalized.slice(4, 6), 16);

    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

/**
 * Build a rounded rectangle path on the canvas context. 
 * @param { CanvasRenderingContext2D } ctx The canvas rendering context to draw on.
 * @param { number } x The x-coordinate of the top-left corner of the rectangle.
 * @param { number } y The y-coordinate of the top-left corner of the rectangle.
 * @param { number } width The width of the rectangle.
 * @param { number } height The height of the rectangle.
 * @param { number } radius The corner radius of the rectangle.
 */
function buildRoundedRectPath(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
): void {
    const safeRadius = Math.min(radius, width / 2, height / 2);

    ctx.beginPath();
    ctx.moveTo(x + safeRadius, y);
    ctx.arcTo(x + width, y, x + width, y + height, safeRadius);
    ctx.arcTo(x + width, y + height, x, y + height, safeRadius);
    ctx.arcTo(x, y + height, x, y, safeRadius);
    ctx.arcTo(x, y, x + width, y, safeRadius);
    ctx.closePath();
}

function drawTooltipConnector(
    ctx: CanvasRenderingContext2D,
    side: 'left' | 'right',
    bodyX: number,
    bodyY: number,
    pointX: number,
    pointY: number
): void {
    const direction = side === 'right' ? 1 : -1;
    const joinX = bodyX;
    const joinTopY = bodyY - 6;
    const joinBottomY = bodyY + 6;
    const midX = joinX + direction * 8;
    const tipX = pointX;
    const tipY = pointY;

    ctx.beginPath();
    ctx.moveTo(joinX, joinTopY);
    ctx.quadraticCurveTo(midX, bodyY - 4, tipX, tipY);
    ctx.quadraticCurveTo(midX, bodyY + 4, joinX, joinBottomY);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
}

/**
 * Drawer for hovered x-axis label decoration and tooltip.
 * @param {Chart<'bar', number[], string>} chart The chart instance.
 * @param {ResolvedXLabelLayout} layout The resolved layout for the hovered label.
 */
function drawHoveredLabel(chart: Chart<'bar', number[], string>, layout: ResolvedXLabelLayout): void {
    const ctx = chart.ctx;
    const horizontalPadding = 12;
    const verticalPadding = 8;
    const pillX = layout.localXMin - horizontalPadding;
    const pillY = layout.textOffset + layout.localYMin - verticalPadding;
    const pillWidth = (layout.localXMax - layout.localXMin) + horizontalPadding * 2;
    const pillHeight = (layout.localYMax - layout.localYMin) + verticalPadding * 2;

    ctx.save();
    ctx.translate(layout.tx, layout.ty);
    ctx.rotate(layout.rotation);

    ctx.shadowColor = 'rgba(15, 23, 42, 0.18)';
    ctx.shadowBlur = 18;
    ctx.shadowOffsetY = 8;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.94)';
    ctx.strokeStyle = 'rgba(59, 130, 246, 0.22)';
    ctx.lineWidth = 1;

    buildRoundedRectPath(ctx, pillX, pillY, pillWidth, pillHeight, 14);
    ctx.fill();
    ctx.stroke();

    ctx.shadowColor = 'transparent';
    ctx.font = layout.font;
    ctx.fillStyle = '#0f172a';
    ctx.textAlign = layout.textAlign ?? 'center';
    ctx.textBaseline = layout.textBaseline ?? 'middle';

    const textX = layout.textAlign === 'left' || layout.textAlign === 'start'
        ? layout.localXMin
        : layout.textAlign === 'right' || layout.textAlign === 'end'
            ? layout.localXMax
            : 0;

    const firstLineY = layout.textOffset + layout.localYMin + layout.lineHeight / 2;

    for (let lineIndex = 0; lineIndex < layout.lines.length; lineIndex++) {
        ctx.fillText(layout.lines[lineIndex], textX, firstLineY + lineIndex * layout.lineHeight);
    }

    ctx.restore();
}

/**
 * Wraps tooltip text into multiple lines based on a maximum character count per line.
 * @param { string } text The original tooltip text.
 * @param { number } maxCharsPerLine The maximum number of characters per line.
 * @returns { string[] } An array of strings, each representing a line of the wrapped text.
 */
function wrapTooltipText(text: string | string[], maxCharsPerLine: number): string[] {
    const lines: string[] = [];
    for (const explicitLine of getLabelLines(text)) {
        const words = explicitLine.split(/\s+/).filter(Boolean);
        if (words.length === 0) {
            lines.push('');
            continue;
        }

        let currentLine = '';
        for (const word of words) {
            const candidate = currentLine.length === 0 ? word : `${currentLine} ${word}`;
            if (candidate.length <= maxCharsPerLine) {
                currentLine = candidate;
                continue;
            }

            if (currentLine.length > 0) {
                lines.push(currentLine);
                currentLine = '';
            }

            if (word.length <= maxCharsPerLine) {
                currentLine = word;
                continue;
            }

            for (let index = 0; index < word.length; index += maxCharsPerLine) {
                lines.push(word.slice(index, index + maxCharsPerLine));
            }
        }

        if (currentLine.length > 0) {
            lines.push(currentLine);
        }
    }

    return lines.length > 0 ? lines : [''];
}

/**
 * Drawer for the hover tooltip shown when hovering over an x-axis label.
 * @param { Chart<'bar', number[], string> } chart The chart instance.
 * @param { string | string[] } label The label(s) to display in the tooltip.
 * @param { { x: number; y: number } } point The coordinates where the tooltip should be drawn.
 */
function drawHoverTooltip(
    chart: Chart<'bar', number[], string>,
    label: string | string[],
    point: { x: number; y: number }
): void {
    const ctx = chart.ctx;
    const canvasWidth = chart.width;
    const canvasHeight = chart.height;
    const lines = wrapTooltipText(label, 40);
    const font = '600 12px Segoe UI Variable, Segoe UI, sans-serif';
    const lineHeight = 19;
    const horizontalPadding = 18;
    const verticalPadding = 14;
    const tooltipOffset = 22;
    const tooltipMaxWidth = 360;
    const tooltipMinWidth = 220;
    const derichebourgBlue = DerichebourgColorEnum.BLUE;
    const derichebourgPaleBlue = DerichebourgColorEnum.PALEBLUE;
    const derichebourgWhite = DerichebourgColorEnum.WHITE;

    ctx.save();
    ctx.font = font;

    let textWidth = 0;
    for (const line of lines) {
        textWidth = Math.max(textWidth, ctx.measureText(line).width);
    }

    const tooltipWidth = Math.max(
        tooltipMinWidth,
        Math.min(textWidth + horizontalPadding * 2, tooltipMaxWidth)
    );
    const tooltipHeight = lines.length * lineHeight + verticalPadding * 2;
    const showOnLeft = point.x + tooltipOffset + tooltipWidth > canvasWidth - 8;
    const tooltipX = showOnLeft
        ? Math.max(8, point.x - tooltipOffset - tooltipWidth)
        : Math.min(canvasWidth - tooltipWidth - 8, point.x + tooltipOffset);
    const tooltipY = Math.min(
        canvasHeight - tooltipHeight - 8,
        Math.max(8, point.y - tooltipHeight - 12)
    );
    const arrowBaseY = Math.min(Math.max(point.y - 4, tooltipY + 14), tooltipY + tooltipHeight - 14);

    ctx.shadowColor = 'rgba(15, 23, 42, 0.12)';
    ctx.shadowBlur = 20;
    ctx.shadowOffsetY = 10;
    ctx.fillStyle = hexToRgba(derichebourgBlue, 0.96);
    ctx.strokeStyle = hexToRgba(derichebourgPaleBlue, 0.65);
    ctx.lineWidth = 1;

    buildRoundedRectPath(ctx, tooltipX, tooltipY, tooltipWidth, tooltipHeight, 14);
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = hexToRgba(derichebourgBlue, 0.96);
    ctx.strokeStyle = hexToRgba(derichebourgPaleBlue, 0.65);
    ctx.lineWidth = 1;

    drawTooltipConnector(
        ctx,
        showOnLeft ? 'right' : 'left',
        showOnLeft ? tooltipX + tooltipWidth : tooltipX,
        arrowBaseY,
        showOnLeft ? Math.min(canvasWidth - 10, point.x - 5) : Math.max(10, point.x + 5),
        point.y - 2
    );

    ctx.shadowColor = 'transparent';
    ctx.fillStyle = derichebourgWhite;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';

    for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
        ctx.fillText(
            lines[lineIndex],
            tooltipX + horizontalPadding,
            tooltipY + verticalPadding + lineHeight / 2 + lineIndex * lineHeight
        );
    }

    ctx.restore();
}

/**
 * Enhances x-axis labels with a hover pill and a custom tooltip.
 *
 * Lifecycle:
 * - `afterEvent`: performs hit-testing and updates hover state.
 * - `afterDraw`: renders hovered label decoration and tooltip from state.
 */
export const xLabelHoverPlugin: Plugin<'bar'> = {
    id: 'xLabelHover',

    afterDraw(chart: Chart<'bar', number[], string>) {
        if (!isPluginEnabled(chart)) {
            return;
        }

        const hoverChart = chart as HoverStateChart;
        const hoveredIndex = hoverChart._xLabelHoverIndex;
        if (hoveredIndex == null) {
            return;
        }

        const xScale = getXCategoryScale(chart);
        if (!xScale) {
            return;
        }

        const layout = resolveXLabelLayout(chart, hoveredIndex, xScale);
        if (!layout) {
            return;
        }

        drawHoveredLabel(chart, layout);

        if (hoverChart._xLabelHoverLabel && hoverChart._xLabelHoverPoint) {
            drawHoverTooltip(chart, hoverChart._xLabelHoverLabel, hoverChart._xLabelHoverPoint);
        }
    },

    afterEvent(chart: Chart<'bar', number[], string>, args: { event: ChartEvent }) {
        const hoverChart = chart as HoverStateChart;
        const event = args.event;

        if (!isPluginEnabled(chart)) {
            if (hoverChart._xLabelHoverIndex != null || hoverChart._xLabelHoverPoint != null || hoverChart._xLabelHoverLabel != null) {
                hoverChart._xLabelHoverIndex = null;
                hoverChart._xLabelHoverLabel = null;
                hoverChart._xLabelHoverPoint = null;
                chart.draw();
            }
            return;
        }

        if (!event) return;

        if (event.type === 'mouseout') {
            if (hoverChart._xLabelHoverIndex != null) {
                hoverChart._xLabelHoverIndex = null;
                hoverChart._xLabelHoverLabel = null;
                hoverChart._xLabelHoverPoint = null;

                const opts = chart.options as HoverPluginOptionsHost;
                opts.plugins?.xLabelHover?.onLabelLeave?.();
                chart.draw();
            }

            if (chart.canvas.style.cursor !== 'default') {
                chart.canvas.style.cursor = 'default';
            }
            return;
        }

        if (event.type !== 'mousemove') return;
        if (event.x == null || event.y == null) return;

        const matches = findMatchingXLabels(chart, event.x, event.y);
        const hoveredMatch = matches.length > 0
            ? matches.reduce((best, current) => current.score < best.score ? current : best)
            : null;

        const hovered = hoveredMatch ? { index: hoveredMatch.index, label: hoveredMatch.label } : null;

        const opts = chart.options as HoverPluginOptionsHost;
        const currentHoverIndex = hoverChart._xLabelHoverIndex;

        if (hovered) {
            if (chart.canvas.style.cursor !== 'pointer') {
                chart.canvas.style.cursor = 'pointer';
            }

            hoverChart._xLabelHoverPoint = { x: event.x, y: event.y };

            if (currentHoverIndex !== hovered.index) {
                hoverChart._xLabelHoverIndex = hovered.index;
                hoverChart._xLabelHoverLabel = hovered.label;
                opts.plugins?.xLabelHover?.onLabelHover?.(
                    hovered.index,
                    hovered.label
                );
            }

            chart.draw();
        } else {
            if (currentHoverIndex != null) {
                hoverChart._xLabelHoverIndex = null;
                hoverChart._xLabelHoverLabel = null;
                hoverChart._xLabelHoverPoint = null;

                opts.plugins?.xLabelHover?.onLabelLeave?.();
                chart.draw();
            }

            if (chart.canvas.style.cursor !== 'default') {
                chart.canvas.style.cursor = 'default';
            }
        }
    }
};

export default xLabelHoverPlugin;
